import tw from 'twin.macro'
import React, { useState, useRef, useEffect, useMemo } from 'react'

import { CgPen, CgInfo } from 'react-icons/cg'
import { IoIosGlobe } from 'react-icons/io'

import { useUpdateExerciseMutation } from 'modules/ExerciseLibrary/exerciseLibraryApi'
import { useUpdatePartsMutation, useUpdateWorkoutMutation } from '../../programApi'

import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useEventListener } from 'common/hooks/useEventListener'

import { maybePluralize } from 'common/utils/stringUtils'
import {
  calcWktEstInDurationForm,
  calculateWorkoutEstimate,
  extractRepsFromText,
  extractSetsCount,
  extractTimeValueFromText,
  getExEstValues,
  getInflationEnabled,
  isPerSideText,
  EX_DURATION_FALLBACK,
  getMobileDisplayTime,
} from 'common/utils/wktDurationUtils'
import Time from 'common/components/Time/Time'
import { UpdateActions } from 'common/components/UpdateActions/UpdateActions'
import './numInputStyles.css'
import { availableEstimateValues, estimateMethods } from './EstimateMethodSelect'
import { inputBase } from 'common/components/Input/Input'
import { insertOneToArray } from 'common/utils/arrayUtils'
import { Switch } from 'common/components/Switch/Switch'
import {
  getReadableBreakdownTime,
  getTimeDurationString,
  normalizeDurationString,
  roundDurationString,
} from 'common/utils/timeDuration'
import { Tooltip } from 'common/components/Tooltip/Tooltip'

function EditWktDurationForm({
  orgId,
  workout,
  orgExercises,
  orgBenchmarks,
  setEditWktDurationModalOpen,
  orgWktDurationInflationEnabled,
}) {
  const {
    exTimes: initExTimes,
    exEstMethods: initEstMethods,
    exReps: initExReps,
    exIsPerSide: initExIsPerSide,
    exWithLowEstConfidence,
  } = getInitExData({ workout, orgExercises, orgBenchmarks })

  const wktTimeEstStr = workout?.timeEst && workout.timeEst !== '00:00:00.00' ? workout.timeEst : null
  const wktInstrTimeEstStr = extractTimeValueFromText(workout?.instructions)
  const authoritativeTimeStr = wktTimeEstStr || wktInstrTimeEstStr

  const [durationEstOverride, setDurationEstOverride] = useState(workout?.durationEstOverride)
  const [durationOverrideEnabled, setDurationOverrideEnabled] = useState(Boolean(durationEstOverride))

  const [inflationEnabled, setInflationEnabled] = useState(
    getInflationEnabled({
      wktDurationInflationEnabled: workout?.enableWktDurationInflation,
      orgWktDurationInflationEnabled,
    })
  )
  const [exTimes, setExTimes] = useState(initExTimes)
  const [exEstMethods, setExEstMethods] = useState(initEstMethods)
  const [exReps, setExReps] = useState(initExReps)
  // Yellow (low-confidence) cards only: the coach clicked the checkmark to keep
  // the prefilled default as their own time. Forces a save for that exercise
  // even though its values are unchanged.
  const [exConfirmed, setExConfirmed] = useState({})
  const [exIsPerSide, setExIsPerSide] = useState(initExIsPerSide)
  // Same comparison handleSubmit's `untouched` check uses: a card whose values
  // differ from its initial state will be saved, so its warning clears live.
  const exEdited = {}
  Object.keys(initExTimes).forEach((key) => {
    exEdited[key] =
      exEstMethods[key] !== initEstMethods[key] ||
      exTimes[key] !== initExTimes[key] ||
      (exEstMethods[key] === estimateMethods.perRep &&
        ((exReps[key] || 1) !== (initExReps[key] || 1) || Boolean(exIsPerSide[key]) !== Boolean(initExIsPerSide[key])))
  })
  const [isLoading, setIsLoading] = useState(false)
  const [inputRefsSortMethod, setInputRefsSortMethod] = useState([])

  const [updateWktParts, { isLoading: isUpdating }] = useUpdatePartsMutation()
  const [updateWorkout, { isLoading: isUpdatingWkt }] = useUpdateWorkoutMutation()

  const parts = workout?.parts || []

  const handleSubmit = async () => {
    setIsLoading(true)
    let partsUpdate = {}
    Object.entries(exEstMethods).forEach(([partIdxExIdxStr, estMethod]) => {
      const [partIdx, exIdx] = partIdxExIdxStr.split('-')
      const exTime = exTimes[partIdxExIdxStr]
      const ex = parts?.[partIdx]?.exercises?.[exIdx]
      const exId = ex?.id
      // Every figure in this form is whole seconds, so stored values are rounded before any
      // comparison — a stored fraction must not read as a difference the coach made.
      const orgExTime = roundDurationString(orgExercises?.[exId]?.timeEst?.perRep)

      // A rep count that matches the exercise's instructions is not stored. Left empty, the
      // estimate re-reads the instructions every time, so editing "10 reps" to "12 reps"
      // later moves the estimate with it. Only a count of the coach's own is frozen.
      const instrText = ex?.type === 'benchmark' ? orgBenchmarks?.[exId]?.instructions : ex?.instructions
      const instrReps = extractRepsFromText(instrText || '')
      const repsValue = exReps[partIdxExIdxStr] || 1
      const repsToStore = instrReps > 0 && repsValue === instrReps ? null : repsValue

      // Same rule for "counts per side", where `false` is an answer and so does get stored.
      const instrIsPerSide = isPerSideText(instrText || '')
      const isPerSideValue = Boolean(exIsPerSide[partIdxExIdxStr])
      const isPerSideToStore = isPerSideValue === instrIsPerSide ? null : isPerSideValue

      // An exercise the coach did not touch keeps whatever estimate state it had. Writing it
      // anyway stamped every untouched figure as the coach's own ("source: coach"), which froze
      // timer- and library-derived estimates that should keep following their source.
      const untouched =
        estMethod === initEstMethods[partIdxExIdxStr] &&
        exTime === initExTimes[partIdxExIdxStr] &&
        (estMethod !== estimateMethods.perRep ||
          ((exReps[partIdxExIdxStr] || 1) === (initExReps[partIdxExIdxStr] || 1) &&
            isPerSideValue === Boolean(initExIsPerSide[partIdxExIdxStr])))
      if (untouched && !exConfirmed[partIdxExIdxStr]) {
        return
      }

      if (estMethod === estimateMethods.perRep && exTime !== orgExTime) {
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/perSet`] = null
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/reps`] = repsToStore
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/isPerSide`] = isPerSideToStore
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/perRep`] = exTime
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/method`] = estMethod
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/source`] = 'coach'
      } else if (estMethod === estimateMethods.perRep && exTime === orgExTime) {
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/perSet`] = null
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/reps`] = repsToStore
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/isPerSide`] = isPerSideToStore
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/perRep`] = null
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/method`] = null
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/source`] = 'coach'
      } else {
        // A figure set back to the exercise's own timer means "follow the timer" — the custom
        // estimate is cleared rather than stored, the same way a per-rep figure equal to the
        // library default follows the library above.
        const timerTime = roundDurationString(ex?.time)
        const matchesTimer = Boolean(timerTime) && timerTime !== '00:00:00.00' && exTime === timerTime
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/reps`] = null
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/isPerSide`] = null
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/perRep`] = null
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/perSet`] = matchesTimer ? null : exTime
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/method`] = matchesTimer ? null : estMethod
        partsUpdate[`${partIdx}/exercises/${exIdx}/timeEst/source`] = 'coach'
      }
    })
    if (Object.keys(partsUpdate).length > 0) {
      await updateWktParts({ orgId, workoutId: workout.id, partsUpdate })
    }

    let wktUpdate = {}
    if (!workout?.durationEstOverride && durationOverrideEnabled) {
      wktUpdate = {
        enableWktDurationInflation: null,
        durationEstOverride,
      }
    } else if (workout?.durationEstOverride && !durationOverrideEnabled) {
      wktUpdate = {
        durationEstOverride: null,
      }
    } else if (
      workout?.durationEstOverride &&
      durationOverrideEnabled &&
      workout.durationEstOverride !== durationEstOverride
    ) {
      wktUpdate = { durationEstOverride }
    }

    if (
      !durationOverrideEnabled &&
      ((workout?.enableWktDurationInflation !== undefined && workout?.enableWktDurationInflation !== null) ||
        orgWktDurationInflationEnabled !== inflationEnabled)
    ) {
      wktUpdate.enableWktDurationInflation = inflationEnabled
    }

    wktUpdate.confidenceScore = null
    wktUpdate.durationEstSource = null

    // Saving IS the coach's review (Vince 8.25): compute what the day-header
    // check will see after this save — same function, applied to the post-save
    // workout — and mark it verified, which clears the deviation and ratio
    // warnings. The legacy hand-typed estimate is retired outright: once the
    // coach has reviewed here, the old number must never re-flag the workout.
    const postSaveWorkout = {
      ...workout,
      parts: applyPartsUpdate(workout?.parts, partsUpdate),
      durationEstOverride: durationOverrideEnabled ? durationEstOverride : null,
      enableWktDurationInflation: inflationEnabled,
      timeEst: null,
    }
    const { totalSeconds: postSaveSeconds } = calculateWorkoutEstimate({
      workout: postSaveWorkout,
      orgExercises,
      orgBenchmarks,
      inflationEnabled,
    })
    wktUpdate.verifiedDurationEst = getTimeDurationString(postSaveSeconds * 1000)
    wktUpdate.timeEst = null

    await updateWorkout({
      orgId,
      workoutId: workout.id,
      workout: wktUpdate,
    })

    setEditWktDurationModalOpen(false)
    setIsLoading(false)
  }

  const { addInputRef, moveFocusOnKeyPress, setLastKnownSortMethod } = useFormRefsControl()
  const totalDurationHrRef = useRef()
  const totalDurationMinRef = useRef()
  const totalDurationSecRef = useRef()

  const submitRef = useRef()
  useEffect(() => {
    addInputRef({
      ref: submitRef,
      name: 'submit',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (durationOverrideEnabled) {
      addInputRef({
        ref: totalDurationHrRef,
        name: 'totalDuration-hr',
      })
      addInputRef({
        ref: totalDurationMinRef,
        name: 'totalDuration-min',
      })
      addInputRef({
        ref: totalDurationSecRef,
        name: 'totalDuration-sec',
      })
      setLastKnownSortMethod(['totalDuration-hr', 'totalDuration-min', 'totalDuration-sec', 'submit'])
      setInputRefsSortMethod(['totalDuration-hr', 'totalDuration-min', 'totalDuration-sec', 'submit'])
    } else {
      let inputRefsSortMethod = []
      Object.keys(initEstMethods).forEach((partIdxExIdx) => {
        inputRefsSortMethod.push(`${partIdxExIdx}-dropdown`)
        inputRefsSortMethod.push(`${partIdxExIdx}-min`)
        inputRefsSortMethod.push(`${partIdxExIdx}-sec`)
      })
      inputRefsSortMethod.push('submit')
      setLastKnownSortMethod(inputRefsSortMethod)
      setInputRefsSortMethod(inputRefsSortMethod)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationOverrideEnabled])

  useEventListener('keydown', (e) => moveFocusOnKeyPress(e, handleSubmit))

  const {
    totalSeconds: wktDurationAutoEst,
    totalOverheadSeconds,
    totalRestSeconds,
    totalWorkSeconds,
  } = useMemo(
    () => calcWktEstInDurationForm({ workout, exReps, exTimes, exEstMethods, exIsPerSide, inflationEnabled }),
    [workout, exReps, exTimes, exEstMethods, exIsPerSide, inflationEnabled]
  )

  const wktDurationReadable = durationOverrideEnabled
    ? getReadableBreakdownTime(durationEstOverride)
    : getReadableBreakdownTime(getTimeDurationString(wktDurationAutoEst * 1000))
  const mobileDisplayTime = getMobileDisplayTime(wktDurationAutoEst)

  return (
    <>
      <div className='overflow-auto'>
        <div className='flex items-center justify-center bg-white rounded-lg justify-self-center p-2 border-2 border-gray-200 mt-8'>
          <button
            css={[
              tw`font-semibold text-sm text-gray-500 hover:text-black transition-all px-6 py-2 rounded-md`,
              !durationOverrideEnabled && tw`bg-gray-100 text-black`,
            ]}
            onClick={() => setDurationOverrideEnabled(false)}
          >
            Auto Estimate
          </button>
          <button
            css={[
              tw`font-semibold text-sm text-gray-500 hover:text-black transition-all px-6 py-2 rounded-md`,
              durationOverrideEnabled && tw`bg-gray-100 text-black`,
            ]}
            onClick={() => {
              if (!durationOverrideEnabled && !durationEstOverride) {
                const estValue = authoritativeTimeStr || getTimeDurationString(wktDurationAutoEst * 1000)
                setDurationEstOverride(estValue)
              }
              setDurationOverrideEnabled(!durationOverrideEnabled)
            }}
          >
            Set Total Time
          </button>
        </div>
        {!durationOverrideEnabled ? (
          <>
            <p className='text-center text-xs text-gray-500 mt-4 px-8'>
              Changes to reps and times here only change the time estimate, not the workout's reps, times, or
              instructions
            </p>
            <div className='mb-4 mt-4 border-y border-gray-200 py-4 px-8'>
              <Switch
                labelClasses='justify-between w-full'
                labelContainerClasses='!text-black'
                onChange={() => setInflationEnabled(!inflationEnabled)}
                label='Include setup & transitions'
                tooltipText={
                  <div className='w-[480px] leading-5'>
                    Automatically include time for setup, transitions and pauses. Turn off to only include base exercise
                    work and rest time.
                  </div>
                }
                isChecked={inflationEnabled}
              />
            </div>
            <div className='px-8 pb-8'>
              {parts.map((part, index) => (
                <Part
                  key={part?.id}
                  orgExercises={orgExercises}
                  orgBenchmarks={orgBenchmarks}
                  part={part}
                  partIdx={index}
                  isLastPart={parts.length - 1 === index}
                  exTimes={exTimes}
                  setExTimes={setExTimes}
                  exEstMethods={exEstMethods}
                  setExEstMethods={setExEstMethods}
                  exReps={exReps}
                  exIsPerSide={exIsPerSide}
                  setExIsPerSide={setExIsPerSide}
                  setExReps={setExReps}
                  exConfirmed={exConfirmed}
                  exEdited={exEdited}
                  setExConfirmed={setExConfirmed}
                  orgId={orgId}
                  inputRefsSortMethod={inputRefsSortMethod}
                  setInputRefsSortMethod={setInputRefsSortMethod}
                  exWithLowEstConfidence={exWithLowEstConfidence}
                />
              ))}
            </div>
          </>
        ) : (
          <div className='p-8 border-t border-gray-200 mt-8'>
            <label className='font-semibold inline-flex cursor-pointer'>Total time</label>
            <p className='text-xs text-gray-500' css={!authoritativeTimeStr && tw`mb-2`}>
              Overrides the automatic estimate with one total workout time.
            </p>
            {authoritativeTimeStr && (
              <p className='mt-2 mb-2 text-xs text-gray-500'>
                The {getReadableBreakdownTime(authoritativeTimeStr)} placeholder value was taken from the workout&apos;s{' '}
                {wktTimeEstStr ? 'previous time estimate.' : 'instructions.'}
              </p>
            )}
            <div className='flex items-center'>
              <Time
                value={durationEstOverride}
                setValue={setDurationEstOverride}
                inputClassNames='!rounded !py-1.5 !px-3 !h-auto !w-auto !text-sm'
                hideLabels={true}
                separatorClassNames='!text-base !mx-1 !pb-4'
                labelClassNames='!text-xs leading-none'
                hrsRef={totalDurationHrRef}
                minsRef={totalDurationMinRef}
                secsRef={totalDurationSecRef}
                onKeyDown={(e) => {
                  e.stopPropagation()
                  moveFocusOnKeyPress(e)
                }}
              />
            </div>
          </div>
        )}
      </div>
      <UpdateActions
        handleSubmit={handleSubmit}
        loading={isLoading || isUpdating || isUpdatingWkt}
        hideDelete={true}
        actionText='Save'
        ref={submitRef}
        contentRight={
          <div className='text-right'>
            {(() => {
              const showsUserLine =
                !durationOverrideEnabled && mobileDisplayTime && mobileDisplayTime !== wktDurationReadable
              const breakdownText =
                !durationOverrideEnabled && wktDurationAutoEst > 0
                  ? [
                      totalWorkSeconds > 0
                        ? `Work: ${getReadableBreakdownTime(getTimeDurationString(totalWorkSeconds * 1000))}`
                        : null,
                      totalRestSeconds > 0
                        ? `Rest: ${getReadableBreakdownTime(getTimeDurationString(totalRestSeconds * 1000))}`
                        : null,
                      inflationEnabled && totalOverheadSeconds > 0
                        ? `Setup & transitions: ${getReadableBreakdownTime(getTimeDurationString(totalOverheadSeconds * 1000))}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' | ')
                  : ''
              const durationLine = (bold) => (
                <div
                  className={
                    bold
                      ? 'font-semibold flex items-center justify-end'
                      : 'text-gray-500 text-xxs flex items-center justify-end'
                  }
                >
                  Workout duration: {wktDurationReadable}
                  {breakdownText && (
                    <Tooltip content={breakdownText} triggerClasses='ml-1 flex items-center'>
                      <CgInfo className='w-3.5 h-3.5 cursor-pointer text-gray-500 hover:text-tGreen' />
                    </Tooltip>
                  )}
                </div>
              )
              return showsUserLine ? (
                <>
                  <div className='font-semibold'>Shown to users: {mobileDisplayTime}</div>
                  {durationLine(false)}
                </>
              ) : (
                durationLine(true)
              )
            })()}
          </div>
        }
      />
    </>
  )
}

function Part({
  orgExercises,
  orgBenchmarks,
  part,
  partIdx,
  isLastPart,
  exTimes,
  setExTimes,
  exEstMethods,
  setExEstMethods,
  exReps,
  exIsPerSide,
  setExIsPerSide,
  setExReps,
  exConfirmed,
  exEdited,
  setExConfirmed,
  orgId,
  inputRefsSortMethod,
  setInputRefsSortMethod,
  exWithLowEstConfidence,
}) {
  const partLetter = String.fromCharCode(65 + partIdx)
  const defaultTitle = part?.name ? partLetter : `Block ${partLetter}`
  const partTitle = part?.name ? part.name : defaultTitle
  const exercises = part?.exercises || []
  const partSets = part.sets || extractSetsCount(part?.instructions || '') || 1

  if (part?.type === 'rest') {
    return (
      <div css={[isLastPart ? tw`mb-0` : tw`mb-5`]}>
        <RestRow name={part?.name} time={part?.time} sets={partSets} />
      </div>
    )
  }

  return (
    <div css={[isLastPart ? tw`mb-0` : tw`mb-5`]}>
      <div className='mb-2'>
        <h2 className='font-semibold'>{partTitle}</h2>
        <div className='text-sm text-gray-500 font-semibold'>
          {partSets} {maybePluralize({ count: partSets, text: 'set' })}
        </div>
      </div>
      {exercises.map((exercise, exIdx) => (
        <React.Fragment key={exercise?.dndUID}>
          <Exercise
            exercise={exercise}
            orgExercises={orgExercises}
            orgBenchmarks={orgBenchmarks}
            exTime={exTimes?.[`${partIdx}-${exIdx}`]}
            setExTimes={setExTimes}
            exEstMethod={exEstMethods?.[`${partIdx}-${exIdx}`]}
            setExEstMethods={setExEstMethods}
            reps={exReps?.[`${partIdx}-${exIdx}`]}
            isPerSide={Boolean(exIsPerSide?.[`${partIdx}-${exIdx}`])}
            setExIsPerSide={setExIsPerSide}
            setExReps={setExReps}
            isConfirmed={Boolean(exConfirmed?.[`${partIdx}-${exIdx}`])}
            isEdited={Boolean(exEdited?.[`${partIdx}-${exIdx}`])}
            setExConfirmed={setExConfirmed}
            partIdx={partIdx}
            exIdx={exIdx}
            orgId={orgId}
            inputRefsSortMethod={inputRefsSortMethod}
            setInputRefsSortMethod={setInputRefsSortMethod}
            hasLowEstConfidence={exWithLowEstConfidence?.[`${partIdx}-${exIdx}`]}
          />
          {Boolean(exercise?.rest) && exercise.rest !== '00:00:00.00' && <RestRow time={exercise.rest} />}
        </React.Fragment>
      ))}
    </div>
  )
}

// A rest the estimate already counts, shown in its place in the flow: a rest block between
// blocks, or an exercise's rest timer after its card. Display only — rest is edited in the
// workout itself, not here.
function RestRow({ name, time, sets = 1 }) {
  return (
    <div className='mb-2 flex items-center justify-between border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-500 font-semibold'>
      <span className='capitalize'>{name || 'Rest'}</span>
      <span>
        {sets > 1 && `${sets} × `}
        {getReadableBreakdownTime(time)}
      </span>
    </div>
  )
}

function Exercise({
  exercise,
  orgExercises,
  orgBenchmarks,
  exTime,
  setExTimes,
  partIdx,
  exIdx = 0,
  exEstMethod,
  setExEstMethods,
  reps,
  setExReps,
  isPerSide,
  setExIsPerSide,
  orgId,
  inputRefsSortMethod,
  setInputRefsSortMethod,
  hasLowEstConfidence,
  isConfirmed,
  isEdited,
  setExConfirmed,
}) {
  const handleMethodChange = (value) => {
    if (value !== exEstMethod) {
      setShowTimePerRepChange(false)
      setShowChangeConfirmation(false)
      setIsEditingReps(false)
      removeInputRef(repsInputRef, `${partIdx}-${exIdx}-reps`)
      setInputRefsSortMethod((state) => state.filter((name) => name !== `${partIdx}-${exIdx}-reps`))

      // Switching methods swaps in the new method's default time, but only
      // when the coach hadn't typed their own value — an untouched default
      // for one method is meaningless under the other (a 45s total is not
      // a 45s per-rep). A typed value is kept: the coach owns it.
      const timerTime = Boolean(time) && time !== '00:00:00.00' ? time : null
      const defaultFor = (method) =>
        method === estimateMethods.perRep
          ? customTimeEstimatePerRep || timeEstimatePerRep || EX_DURATION_FALLBACK
          : customTimeEstimatePerSet || timerTime || EX_DURATION_FALLBACK
      if (exTime === defaultFor(exEstMethod)) {
        setExTimes((state) => ({ ...state, [`${partIdx}-${exIdx}`]: defaultFor(value) }))
      }
    }
    setExEstMethods((state) => ({ ...state, [`${partIdx}-${exIdx}`]: value }))
  }

  // One orange unit: border, explanation, and the keep-button appear together
  // and disappear together — on confirm, or the moment the coach edits.
  const showsLowConfidenceWarning = hasLowEstConfidence && !isConfirmed && !isEdited

  const estMethodInputRef = useRef()
  const minInputRef = useRef()
  const secInputRef = useRef()
  const repsInputRef = useRef()

  // Rounded on read like every stored duration in this form: these are compared against the
  // whole-second values the time inputs produce, and a stored fraction is not a custom time.
  const timeEstimatePerRep = roundDurationString(orgExercises?.[exercise?.id]?.timeEst?.perRep)
  const customTimeEstimatePerRep = roundDurationString(exercise?.timeEst?.perRep)
  const customTimeEstimatePerSet = exercise?.timeEst?.perSet

  const initialTimeEstPerRepRef = useRef(customTimeEstimatePerRep || timeEstimatePerRep || '00:00:00.00')
  const [showChangeConfirmation, setShowChangeConfirmation] = useState(false)
  const [showTimePerRepChange, setShowTimePerRepChange] = useState(false)
  const [isEditingReps, setIsEditingReps] = useState(false)

  const [updateOrgExercise] = useUpdateExerciseMutation()

  const { addInputRef, removeInputRef, moveFocusOnKeyPress } = useFormRefsControl()

  const orgExExists = Boolean(orgExercises?.[exercise?.id])
  const time = roundDurationString(exercise?.time)
  const exTimeExists = Boolean(time) && time !== '00:00:00.00'
  const [, timeMin, timeSec] = exTimeExists ? normalizeDurationString(time).split(':') : ['00', '00', '00']

  const timeEstLabelText = availableEstimateValues.find((est) => est.value === exEstMethod)?.displayValue

  // The rep count the exercise's own instructions state, and a way back to it once the
  // figure has been moved off. Reps equal to the instructions are not stored on save, so
  // they keep following the instructions if those are edited later.
  const instrText =
    exercise?.type === 'benchmark' ? orgBenchmarks?.[exercise?.id]?.instructions : exercise?.instructions
  const instrReps = extractRepsFromText(instrText || '')
  const useInstructionsButton = exEstMethod === estimateMethods.perRep &&
    instrReps > 0 &&
    (reps || 1) !== instrReps && (
      <button
        type='button'
        className='ml-3 bg-gray-200 hover:ring-2 hover:ring-tGray-ml text-gray-600 rounded px-2 py-1 transition-all text-xxs leading-normal font-medium'
        onClick={() => setExReps((state) => ({ ...state, [`${partIdx}-${exIdx}`]: instrReps }))}
      >
        Use instructions
      </button>
    )

  // When the library per-rep CHANGES (a sibling card's "Entire app" confirm),
  // non-custom cards of the same exercise follow it. Skips the initial mount:
  // enforcing the library there overrode higher-priority sources — an
  // instruction-derived 40s snapped to the library's 3s right after opening,
  // silently changing the estimate the modal showed vs the stored workout.
  const prevOrgPerRepRef = useRef(timeEstimatePerRep)
  useEffect(() => {
    if (prevOrgPerRepRef.current === timeEstimatePerRep) {
      return
    }
    prevOrgPerRepRef.current = timeEstimatePerRep
    if (
      Boolean(timeEstimatePerRep) &&
      timeEstimatePerRep !== exTime &&
      exEstMethod === estimateMethods.perRep &&
      !customTimeEstimatePerRep
    ) {
      setExTimes((state) => ({ ...state, [`${partIdx}-${exIdx}`]: timeEstimatePerRep }))
    }
    // eslint-disable-next-line
  }, [timeEstimatePerRep])

  useEffect(() => {
    addInputRef({
      ref: estMethodInputRef,
      name: `${partIdx}-${exIdx}-dropdown`,
    })
    addInputRef({
      ref: minInputRef,
      name: `${partIdx}-${exIdx}-min`,
    })
    addInputRef({
      ref: secInputRef,
      name: `${partIdx}-${exIdx}-sec`,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className='mb-2 border border-gray-300 rounded-md overflow-hidden'
      css={[showsLowConfidenceWarning && tw`border-orange-300`]}
    >
      <div className='bg-gray-100 p-3'>
        <h3
          className='font-semibold capitalize text-gray-700 line-clamp-1'
          title={exercise?.name || 'Untitled Exercise'}
        >
          {exIdx + 1}. {exercise?.name || 'Untitled Exercise'}
        </h3>
        {Boolean(instrText?.trim()) && (
          <p className='mt-0.5 text-xs text-gray-500 line-clamp-2' title={instrText.trim()}>
            {instrText.trim()}
          </p>
        )}
        {showsLowConfidenceWarning && (
          <p className='mt-1 mb-2 text-xs text-orange-500'>
            No reliable time found. Enter a time below, or{' '}
            <button
              type='button'
              className='underline hover:text-tGreen transition-colors'
              onClick={() => setExConfirmed((state) => ({ ...state, [`${partIdx}-${exIdx}`]: true }))}
            >
              keep the {getReadableBreakdownTime(exTime)} estimate
            </button>
          </p>
        )}
      </div>
      <div className='p-3'>
        <div className='mb-3 text-center'>
          <label className='font-semibold text-sm mb-1 inline-flex'>Estimate method</label>
          <div className='flex justify-center'>
            <div className='inline-flex items-center bg-white rounded-lg border border-gray-200 p-1'>
              <button
                type='button'
                ref={estMethodInputRef}
                css={[
                  tw`font-semibold text-sm text-gray-500 hover:text-black transition-all px-4 py-1.5 rounded-md`,
                  exEstMethod === estimateMethods.perRep && tw`bg-gray-100 text-black`,
                ]}
                onClick={() => handleMethodChange(estimateMethods.perRep)}
              >
                Time per rep
              </button>
              <button
                type='button'
                css={[
                  tw`font-semibold text-sm text-gray-500 hover:text-black transition-all px-4 py-1.5 rounded-md`,
                  exEstMethod === estimateMethods.perSet && tw`bg-gray-100 text-black`,
                ]}
                onClick={() => handleMethodChange(estimateMethods.perSet)}
              >
                Total time
              </button>
            </div>
          </div>
        </div>
        <div className='space-y-3'>
          {exEstMethod === estimateMethods.perRep && !isEditingReps && (
            <p className='flex items-center text-sm text-tBlack font-semibold'>
              {reps} {maybePluralize({ count: reps, text: 'rep' })}{' '}
              <CgPen
                className='cursor-pointer w-3.5 h-3.5 text-gray-500 hover:text-tGreen ml-1'
                onClick={() => setIsEditingReps(true)}
              />
              {useInstructionsButton}
            </p>
          )}
          {exEstMethod === estimateMethods.perRep && isEditingReps && (
            <div>
              <RepsInput
                repsInputRef={repsInputRef}
                reps={reps}
                setExReps={setExReps}
                partIdx={partIdx}
                exIdx={exIdx}
                inputRefsSortMethod={inputRefsSortMethod}
                setInputRefsSortMethod={setInputRefsSortMethod}
                trailing={useInstructionsButton}
              />
            </div>
          )}
          {/* Shown only where it means something: the shared per-side regex (the same
          one that auto-checks it and that the duration script applies) detects
          per-side wording, or the coach already stored an override — either value,
          so a stored false stays visible and editable. Wording the regex misses is
          fixed by editing the instructions, which members read too. */}
          {exEstMethod === estimateMethods.perRep &&
            (isPerSideText(instrText || '') || exercise?.timeEst?.isPerSide != null) && (
              <div className='flex items-center'>
                <input
                  type='checkbox'
                  id={`${partIdx}-${exIdx}-isPerSide`}
                  name={`${partIdx}-${exIdx}-isPerSide`}
                  className='cursor-pointer rounded-sm border-black border-opacity-20 text-tGreen focus:ring-0'
                  checked={isPerSide}
                  onChange={(e) => setExIsPerSide((state) => ({ ...state, [`${partIdx}-${exIdx}`]: e.target.checked }))}
                />
                <label htmlFor={`${partIdx}-${exIdx}-isPerSide`} className='cursor-pointer text-sm font-semibold ml-2'>
                  Reps are per side (×2)
                </label>
                <Tooltip
                  content={`If the rep count above (${reps || 10} reps) is for each side, check this box to double the time estimate.`}
                  side='bottom'
                  triggerClasses='ml-1 flex items-center'
                >
                  <CgInfo className='w-4 h-4 cursor-pointer text-gray-500 hover:text-tGreen' />
                </Tooltip>
              </div>
            )}
          <div>
            <label
              className='font-semibold text-sm mb-1 inline-flex cursor-pointer'
              onClick={() => {
                minInputRef?.current?.focus?.()
              }}
            >
              {timeEstLabelText}
            </label>
            <div className='flex items-center'>
              <Time
                value={exTime}
                setValue={(value) => {
                  setExTimes((state) => ({ ...state, [`${partIdx}-${exIdx}`]: value }))

                  if (!orgExExists || exEstMethod !== estimateMethods.perRep) {
                    return
                  }

                  if (
                    initialTimeEstPerRepRef?.current !== value &&
                    value !== timeEstimatePerRep &&
                    !showTimePerRepChange
                  ) {
                    setShowTimePerRepChange(true)
                    setShowChangeConfirmation(false)
                  } else if (value === timeEstimatePerRep && (showTimePerRepChange || showChangeConfirmation)) {
                    setShowTimePerRepChange(false)
                    setShowChangeConfirmation(false)
                  }
                }}
                inputClassNames='!rounded !py-1.5 !px-3 !h-auto !w-auto !text-sm'
                hideLabels={true}
                separatorClassNames='!text-base !mx-1 !pb-4'
                labelClassNames='!text-xs leading-none'
                timeTypes={['mins', 'secs']}
                minsRef={minInputRef}
                secsRef={secInputRef}
                onKeyDown={(e) => {
                  e.stopPropagation()
                  moveFocusOnKeyPress(e)
                }}
              />
              {exEstMethod === estimateMethods.perRep && timeEstimatePerRep !== exTime && (
                <div className='ml-3 mb-4 flex items-center bg-gray-200 text-gray-600 rounded font-medium text-xxs transition-all'>
                  <Tooltip
                    content='This exercise’s time per rep is different than the app’s default value'
                    // The clear-X supplies the pill's right padding; without one (no library
                    // default to clear back to), the pill pads its own right edge.
                    triggerClasses={`group cursor-pointer flex items-center pl-2 py-1${
                      timeEstimatePerRep ? '' : ' pr-2'
                    }`}
                  >
                    <div>custom time</div>
                    <CgInfo className='ml-1 w-3.5 h-3.5 group-hover:text-tGreen' />
                  </Tooltip>
                  {Boolean(timeEstimatePerRep) && (
                    <Tooltip
                      content={`Clear and use the app default (${getReadableBreakdownTime(timeEstimatePerRep)})`}
                    >
                      <button
                        type='button'
                        aria-label='Clear custom time'
                        className='px-1.5 py-1 hover:text-red-500 transition-colors'
                        onClick={() =>
                          setExTimes((state) => ({ ...state, [`${partIdx}-${exIdx}`]: timeEstimatePerRep }))
                        }
                      >
                        ×
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}
              {exEstMethod === estimateMethods.perSet && exTimeExists && exTime === time && (
                <Tooltip
                  content={`This total follows the exercise's ${timeMin}:${timeSec.replace(/\.00$/, '')} timer. Edit it to override.`}
                  triggerClasses='ml-3 mb-4 group cursor-pointer flex items-center bg-gray-200 hover:ring-2 hover:ring-tGray-ml text-gray-600 px-2 py-1 rounded font-medium text-xxs transition-all'
                >
                  <div>from timer</div>
                  <CgInfo className='ml-1 w-3.5 h-3.5 group-hover:text-tGreen' />
                </Tooltip>
              )}
              {exEstMethod === estimateMethods.perSet && exTimeExists && exTime !== time && (
                <button
                  type='button'
                  className='ml-3 mb-4 bg-gray-200 hover:ring-2 hover:ring-tGray-ml text-gray-600 rounded px-2 py-1 transition-all text-xxs font-medium'
                  onClick={() => setExTimes((state) => ({ ...state, [`${partIdx}-${exIdx}`]: time }))}
                >
                  Use timer
                </button>
              )}
            </div>
          </div>
          {showTimePerRepChange && (
            <div className='p-2 bg-gray-100 rounded-md mt-2'>
              <div className='flex items-center mb-1'>
                <IoIosGlobe className='text-gray-500 w-5 h-5' />
                <h5 className='font-semibold text-sm ml-2'>Apply this change everywhere?</h5>
              </div>
              <p className='text-sm'>
                This will update the default time-per-rep for{' '}
                <span className='font-semibold capitalize'>{exercise?.name || 'Untitled Exercise'}</span> across all
                workouts in your app.
              </p>
              <div className='mt-2'>
                <button
                  type='button'
                  className='border-gray-600 hover:border-tGreen border text-gray-600 rounded px-2 py-1.5 transition-colors text-xs font-semibold'
                  onClick={() => {
                    setShowTimePerRepChange(false)
                  }}
                >
                  Only this exercise
                </button>
                <button
                  type='button'
                  className='border-gray-600 hover:border-tGreen border text-gray-600 rounded px-2 py-1.5 transition-colors text-xs font-semibold ml-2'
                  onClick={() => {
                    setShowTimePerRepChange(false)
                    setShowChangeConfirmation(true)
                  }}
                >
                  Entire app
                </button>
              </div>
            </div>
          )}
          {showChangeConfirmation && (
            <div className='p-2 bg-gray-100 rounded-md mt-2'>
              <div className='flex items-center mb-1'>
                <IoIosGlobe className='text-gray-500 w-5 h-5' />
                <h5 className='font-semibold text-sm ml-2'>Are you sure?</h5>
              </div>
              <p className='text-sm'>
                The default time for{' '}
                <span className='font-semibold capitalize'>{exercise?.name || 'Untitled Exercise'}</span> will be
                updated to <span className='font-semibold capitalize'>{getReadableBreakdownTime(exTime)}</span> for all
                workouts of your app. Exercises with a custom time per rep will not be changed.
              </p>
              <div className='mt-2'>
                <button
                  type='button'
                  className='border-gray-600 hover:border-tGreen border text-gray-600 rounded px-2 py-1.5 transition-colors text-xs font-semibold'
                  onClick={() => {
                    setShowChangeConfirmation(false)
                    setShowTimePerRepChange(true)
                  }}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='border-gray-600 hover:border-tGreen border text-gray-600 rounded px-2 py-1.5 transition-colors text-xs font-semibold ml-2'
                  onClick={() => {
                    updateOrgExercise({
                      coachOrgId: orgId,
                      exerciseKey: exercise?.id,
                      exercise: {
                        timeEst: {
                          perRep: exTime,
                          source: 'coach',
                        },
                      },
                    })
                    // Updating the entire app IS the coach acting on this estimate. Without
                    // this, the typed time becomes the new library default, the card reads as
                    // untouched, Save silently skips it, and the yellow warning never clears
                    // (seen live: Spinal Articulations, 08.26). Confirming clears the warning
                    // and makes Save store the rep count, which is what ends the low-confidence
                    // state for an exercise whose instructions carry no reps.
                    setExConfirmed((state) => ({ ...state, [`${partIdx}-${exIdx}`]: true }))
                    setShowChangeConfirmation(false)
                    setShowTimePerRepChange(false)
                    initialTimeEstPerRepRef.current = exTime
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function RepsInput({
  repsInputRef,
  reps,
  setExReps,
  partIdx,
  exIdx,
  inputRefsSortMethod,
  setInputRefsSortMethod,
  trailing,
}) {
  const { addInputRef, moveFocusOnKeyPress, setLastKnownSortMethod } = useFormRefsControl()

  const handleRepsChange = (e) => {
    if (!e.target.value) {
      setExReps((state) => ({ ...state, [`${partIdx}-${exIdx}`]: 0 }))
    }

    if (Number(e.target.value) < 999 && Number(e.target.value) >= 0) {
      setExReps((state) => ({ ...state, [`${partIdx}-${exIdx}`]: parseInt(Number(e.target.value)) }))
    }
  }

  useEffect(() => {
    const insertToIdx = inputRefsSortMethod.findIndex((name) => name === `${partIdx}-${exIdx}-dropdown`)
    const updatedRefsSort = insertOneToArray(inputRefsSortMethod, insertToIdx, `${partIdx}-${exIdx}-reps`)

    addInputRef({
      ref: repsInputRef,
      name: `${partIdx}-${exIdx}-reps`,
    })
    setLastKnownSortMethod(updatedRefsSort)
    setInputRefsSortMethod(updatedRefsSort)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <label
        className='font-semibold text-sm mb-1 inline-flex cursor-pointer'
        onClick={() => {
          repsInputRef?.current?.focus?.()
        }}
      >
        Reps
      </label>
      {/* The trailing button rides in the same row as the input — as a sibling of the block
          input it would wrap underneath instead of sitting beside it. */}
      <div className='flex items-center'>
        <input
          name='reps'
          id='reps'
          type='number'
          placeholder='0'
          min={0}
          max={999}
          value={(reps || 0).toString()}
          onChange={handleRepsChange}
          onBlur={(e) => {
            setExReps((state) => ({ ...state, [`${partIdx}-${exIdx}`]: Number(e.target.value) || 1 }))
          }}
          onKeyDown={(e) => {
            e.stopPropagation()
            moveFocusOnKeyPress(e)
          }}
          ref={repsInputRef}
          css={[inputBase, tw`text-center rounded py-1.5 px-3 h-auto w-auto !text-sm`]}
        />
        {trailing}
      </div>
    </>
  )
}

function getInitExData({ workout, orgExercises, orgBenchmarks }) {
  let exTimes = {}
  let exEstMethods = {}
  let exReps = {}
  let exIsPerSide = {}
  let exWithLowEstConfidence = {}

  const parts = workout?.parts || []
  parts.forEach((part, partIdx) => {
    if (part?.type === 'rest') {
      return
    }

    const exercises = part?.exercises || []
    exercises.forEach((exercise, exIdx) => {
      const { timeEstValue, timeEstMethod, isLowConfidence, reps, isPerSide } = getExEstValues({
        exercise,
        orgBenchmarks,
        orgExercises,
      })
      exReps[`${partIdx}-${exIdx}`] = Math.max(reps, 1)
      exTimes[`${partIdx}-${exIdx}`] = timeEstValue
      exEstMethods[`${partIdx}-${exIdx}`] = timeEstMethod
      exIsPerSide[`${partIdx}-${exIdx}`] = isPerSide
      if (isLowConfidence) {
        exWithLowEstConfidence[`${partIdx}-${exIdx}`] = true
      }
    })
  })

  return { exTimes, exEstMethods, exReps, exIsPerSide, exWithLowEstConfidence }
}

// Applies handleSubmit's slash-path parts update to a cloned parts array, so the
// post-save estimate can be computed with the exact data the save will produce.
function applyPartsUpdate(parts, partsUpdate) {
  const cloned = JSON.parse(JSON.stringify(parts || []))
  Object.entries(partsUpdate).forEach(([path, value]) => {
    const keys = path.split('/')
    let node = cloned
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      if (node[key] == null) {
        node[key] = {}
      }
      node = node[key]
    }
    node[keys[keys.length - 1]] = value
  })
  return cloned
}

export default EditWktDurationForm
